'use client'

import React, { Component, ReactNode, ErrorInfo } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

/**
 * Error Boundary to catch infinite loops and other React errors
 *
 * Captures component stack traces and sends to error tracking
 * Shows user-friendly fallback UI with recovery options
 */

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  errorCount: number
}

export class InfiniteLoopBoundary extends Component<Props, State> {
  private errorTimestamps: number[] = []

  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const now = Date.now()
    this.errorTimestamps.push(now)

    // Keep only errors from last 5 seconds
    this.errorTimestamps = this.errorTimestamps.filter((ts) => now - ts < 5000)

    // Update state with error details
    this.setState((prev) => ({
      error,
      errorInfo,
      errorCount: prev.errorCount + 1,
    }))

    // Log error details
    console.error('🚨 Error Boundary caught error:', error)
    console.error('Component stack:', errorInfo.componentStack)

    // Detect infinite loop pattern (multiple errors in short time)
    const isInfiniteLoop = this.errorTimestamps.length > 3

    if (isInfiniteLoop) {
      console.error(
        `⚠️ INFINITE LOOP PATTERN DETECTED: ${this.errorTimestamps.length} errors in 5 seconds`
      )
    }

    // Send to error tracking
    if (typeof window !== 'undefined') {
      // BugSnag
      if (window.Bugsnag) {
        window.Bugsnag.notify(error, (event) => {
          event.severity = isInfiniteLoop ? 'error' : 'warning'
          event.addMetadata('react', {
            componentStack: errorInfo.componentStack,
            errorCount: this.state.errorCount,
            isInfiniteLoop,
            recentErrors: this.errorTimestamps.length,
          })
        })
      }
    }

    // Call custom error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  handleReset = () => {
    // Clear error timestamps
    this.errorTimestamps = []

    // Reset state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    })
  }

  handleReload = () => {
    // Full page reload (last resort)
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      const { error, errorInfo, errorCount } = this.state
      const isInfiniteLoop = this.errorTimestamps.length > 3

      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <div className="w-full max-w-2xl">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle className="text-lg font-semibold">
                {isInfiniteLoop ? '🔄 Infinite Loop Detected' : 'Something went wrong'}
              </AlertTitle>
              <AlertDescription className="mt-3 space-y-4">
                <p className="text-sm">
                  {isInfiniteLoop
                    ? 'This component is stuck in an infinite render loop. The error has been logged for investigation.'
                    : 'An unexpected error occurred. Your data is safe, but this component cannot be displayed.'}
                </p>

                {process.env.NODE_ENV === 'development' && (
                  <>
                    <div className="mt-4 p-3 bg-destructive/10 rounded-md">
                      <p className="text-xs font-mono font-semibold mb-1">Error Details:</p>
                      <p className="text-xs font-mono text-red-800 dark:text-red-200">
                        {error?.message}
                      </p>
                      {errorCount > 1 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          (Error count: {errorCount} | Recent errors: {this.errorTimestamps.length})
                        </p>
                      )}
                    </div>

                    {errorInfo?.componentStack && (
                      <details className="mt-3">
                        <summary className="text-xs font-semibold cursor-pointer hover:underline">
                          Component Stack Trace
                        </summary>
                        <pre className="mt-2 p-3 bg-muted rounded-md text-xs overflow-x-auto">
                          {errorInfo.componentStack}
                        </pre>
                      </details>
                    )}

                    {error?.stack && (
                      <details className="mt-3">
                        <summary className="text-xs font-semibold cursor-pointer hover:underline">
                          JavaScript Stack Trace
                        </summary>
                        <pre className="mt-2 p-3 bg-muted rounded-md text-xs overflow-x-auto">
                          {error.stack}
                        </pre>
                      </details>
                    )}
                  </>
                )}

                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={this.handleReset}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Try Again
                  </Button>

                  {isInfiniteLoop && (
                    <Button onClick={this.handleReload} variant="destructive" size="sm">
                      Reload Page
                    </Button>
                  )}
                </div>

                <p className="text-xs text-muted-foreground mt-4">
                  Error ID: {Date.now().toString(36)}
                  {process.env.NODE_ENV === 'production' &&
                    ' - This error has been reported to our team.'}
                </p>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Lightweight error boundary for specific components
 * Shows minimal fallback UI
 */
export class ComponentErrorBoundary extends Component<
  { children: ReactNode; componentName?: string },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; componentName?: string }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const componentName = this.props.componentName || 'Component'
    console.error(`Error in ${componentName}:`, error)
    console.error('Component stack:', errorInfo.componentStack)

    // Report to error tracking
    if (typeof window !== 'undefined' && window.Bugsnag) {
      window.Bugsnag.notify(error, (event) => {
        event.addMetadata('react', {
          component: componentName,
          componentStack: errorInfo.componentStack,
        })
      })
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 border border-destructive rounded-md bg-destructive/5">
          <p className="text-sm text-destructive">
            {this.props.componentName || 'This component'} failed to load.
          </p>
        </div>
      )
    }

    return this.props.children
  }
}

// Type augmentation
declare global {
  interface Window {
    Bugsnag?: {
      notify: (error: Error, callback?: (event: any) => void) => void
    }
  }
}
